/**
 * canvas-engine/presets/decompositionAdapters/education.js
 *
 * Education family adapter. Education sections (hero, category/feature grids,
 * featured courses, mission/vision, community CTA, info/about/contact shells)
 * decompose faithfully with the core engine's vertical-stack layout, so most
 * delegate straight to `buildSectionCanvas`.
 *
 * The only family tweak is the faithful "split" arrangement for two-column
 * sections that carry a single foreground image (e.g. the community CTA): the
 * image is placed beside the lead copy instead of below it. Full-bleed heroes
 * (which use a background image) stay stacked.
 *
 * The adapter accepts EITHER a transcribed `EDUCATION_PRESETS` descriptor OR a
 * section-type string looked up in `EDUCATION_PRESETS`, so the migration
 * (Task 4.1) and preset registry (Task 3.4) can dispatch by type. It always
 * lays out at the fixed `Reference_Width` with `idPrefix = sectionType`.
 *
 * AUTH VARIANT PASSTHROUGH: `options.authVariant` is forwarded verbatim to
 * `buildSectionCanvas` via `adapterOptions(...)` (which spreads `...opts`), so
 * `decomposeSection('educationAuthEntry', { authVariant: 'signUp' })` makes the
 * engine merge `content.variants.signUp` over `content` before layout — i.e.
 * the `educationAuthEntry` preset decomposes its sign-up copy/fields for the
 * sign-up page, while the default sign-in copy is used otherwise. No explicit
 * threading is needed here: the option rides along in the options object.
 *
 * SECTION-TYPE COVERAGE: `EDUCATION_TYPES` auto-derives from
 * `handledTypesOf(EDUCATION_PRESETS)`, so the new Education info-page
 * sectionTypes (`educationBridgingKnowledgeGap`, `educationInsightsEngagement`,
 * `educationIndustryExperience`, `educationGroomingSkills`) are handled
 * automatically the moment their descriptors are added to `EDUCATION_PRESETS`
 * — no manual list edits required.
 *
 * ZERO-REUSE CONSTRAINT: imports ONLY sibling engine modules + transcribed
 * presetData VALUES. Never emits a `native` element.
 *
 * Requirements: 4.1, 5.1, 5.2, 5.6, 5.7
 */

import { REFERENCE_FRAME_WIDTH } from './buildSectionCanvas';
import { buildFamilyCanvas, wantsSplit } from './familyLayout';
import { resolvePreset, adapterOptions, handledTypesOf } from './resolvePreset';
import { EDUCATION_PRESETS } from '../presetData/educationPresets';

/** The Education section types this adapter handles (keys + sectionTypes). */
export const EDUCATION_TYPES = Object.freeze(handledTypesOf(EDUCATION_PRESETS));

/** True when `sectionType` is decomposed by the Education adapter. */
export function handlesEducationType(sectionType) {
  return EDUCATION_TYPES.includes(typeof sectionType === 'string' ? sectionType.trim() : sectionType);
}

/**
 * Decompose an Education section into a normalized Canvas_Model.
 *
 * @param {(string|object)} sectionTypeOrData an `EDUCATION_PRESETS` key /
 *   sectionType, or a transcribed Education preset DATA descriptor
 * @param {object} [options] `buildSectionCanvas` options. Notably
 *   `options.authVariant` (e.g. `'signUp'`) is forwarded as-is so the
 *   `educationAuthEntry` preset can decompose its `content.variants.signUp`.
 * @returns {object} a normalized Canvas_Model
 */
export function educationToCanvas(sectionTypeOrData, options = {}) {
  const { data, sectionType } = resolvePreset(sectionTypeOrData, EDUCATION_PRESETS, options);
  // adapterOptions spreads `...options` (incl. authVariant) into the build opts.
  const opts = adapterOptions(sectionType, REFERENCE_FRAME_WIDTH, options);
  return buildFamilyCanvas(data, opts, { split: wantsSplit(data) });
}

export default educationToCanvas;
